/**
 * WS-4 (fieldiq-api#1294) — the display rules for the Well Servicing rate card
 * and field tickets, carried over from WT-4 (lib/wt-tickets.ts) with WS's codes. Pure functions; the screens import these so a refusal, a
 * status hue and who may press what read the same everywhere.
 *
 * THE API DECIDES. Nothing here prices a line or adds up a ticket: the line
 * amount, the draft subtotal, the tax and the total are the API's figures. The
 * role lists mirror fieldiq-api lib/ws-roles.ts for hiding buttons only; a 403
 * that still arrives is said as a refusal.
 */
import { wsDay, wsJobRefusal, type WsSkin } from './well-servicing';

const WS_UNKNOWN_SKIN: WsSkin = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };
export type WsRefusalOverrides = Record<string, (x: Record<string, unknown>) => string>;

// ─── Who may press what (mirrors WS_TICKET_READ_ROLES / WS_TICKET_WRITE_ROLES) ─

export const WS_TICKET_READ_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher'] as const;
export const WS_TICKET_WRITE_ROLES = ['admin', 'office_staff'] as const;

const inList = (list: readonly string[]) => (role: string | null | undefined) => !!role && list.includes(role);
/** The rate card and field tickets carry prices: the office, the supervisor and dispatch read them. A field hand does not. */
export const canReadWsTickets = inList(WS_TICKET_READ_ROLES);
/** Writing the card, drafting, editing, approving, sending, recording an approval, voiding — the office. */
export const canWriteWsTickets = inList(WS_TICKET_WRITE_ROLES);

// ─── Status ──────────────────────────────────────────────────────────────────

/** Draft slate, office-approved blue (waiting on the customer), customer-approved green, rejected red, void steel. */
export const WS_TICKET_STATUS_SKIN: Record<string, WsSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  office_approved: { label: 'Office approved', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  customer_approved: { label: 'Customer approved', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  customer_rejected: { label: 'Customer rejected', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  void: { label: 'Void', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' },
};
export const wsTicketStatusSkin = (status: string | null | undefined): WsSkin =>
  status && Object.prototype.hasOwnProperty.call(WS_TICKET_STATUS_SKIN, status) ? WS_TICKET_STATUS_SKIN[status] : WS_UNKNOWN_SKIN;

/** Statuses the PDF renders for (the API answers 409 for a draft or a void ticket). */
export const WS_TICKET_PDF = ['office_approved', 'customer_approved', 'customer_rejected'];
export const wsTicketHasPdf = (status: string | null | undefined) => !!status && WS_TICKET_PDF.includes(status);

/**
 * WHICH TICKETS MAY BE EDITED, AND HOW (the API's ticketForEdit).
 *
 *   draft              lines and header edit freely
 *   office_approved    an edit RETURNS IT TO DRAFT (the office approval, the
 *   customer_rejected  review link and the totals are withdrawn) — so the
 *                      screen asks first, and says so when the API reports
 *                      `reverted_to_draft`
 *   customer_approved  frozen; only the internal notes change
 *   void               frozen entirely, internal notes included
 */
export type WsTicketEditMode = 'free' | 'reverts' | 'frozen' | 'void';
export function wsTicketEditMode(status: string | null | undefined): WsTicketEditMode {
  if (status === 'draft') return 'free';
  if (status === 'office_approved' || status === 'customer_rejected') return 'reverts';
  if (status === 'void') return 'void';
  return 'frozen';
}

/** Delete is refused on a customer-approved or a void ticket (keep the record). */
export const wsTicketDeletable = (status: string | null | undefined) =>
  !!status && status !== 'customer_approved' && status !== 'void';

/** The sentence a `reverted_to_draft: true` echo is said with. Never in a success style. */
export const WS_REVERTED_NOTICE =
  'This change returned the ticket to draft. The office approval was withdrawn, and any review link sent to the customer no longer works. Approve it again before sending it.';

/** What a refresh from the tour sheets does to the lines — said BEFORE the office presses it. */
export const WS_REFRESH_WARNING =
  'Refreshing redraws every line drafted from the tour sheets. A price the office set on one of those lines is lost; lines the office added are kept.';

/** What voiding does — said before the office presses it. */
export const WS_VOID_WARNING =
  'Voiding keeps the ticket on file but stops it billing. Any review link sent to the customer stops working, and a void ticket cannot be edited or un-voided.';

export function wsScopeText(t: { scope: string; workDate: string | null }): string {
  if (t.scope === 'day') return t.workDate ? wsDay(t.workDate) : 'One day';
  if (t.scope === 'job') return 'Whole job';
  return '—';
}

export const WS_APPROVAL_SOURCE_TEXT: Record<string, string> = {
  link: 'signed by the company man on the review link',
  office_upload: 'recorded by the office from the photo of the signed paper ticket',
};

/** `Well name · UWI 100/…` — the identifier the tenant's country uses, whichever is stored. */
export function wsTicketWellText(t: { wellName: string | null; wellUwi: string | null; wellApiNumber: string | null }): string {
  const id = t.wellUwi ? `UWI ${t.wellUwi}` : t.wellApiNumber ? `API number ${t.wellApiNumber}` : null;
  return [t.wellName, id].filter(Boolean).join(' · ') || '—';
}

// ─── Refusals ────────────────────────────────────────────────────────────────

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/**
 * Every refusal code the WS-4 routes send, as one plain sentence. Passed to
 * wsTicketRefusal; a code this map lacks falls back to the WS-2
 * map, then a 404/403 sentence, then the API's own `error`.
 */
export const WS_TICKET_REFUSALS: WsRefusalOverrides = {
  // rate card
  rate_overlap: () =>
    'Another rate with this code already covers some of these days for the same customer (or the default card). End that rate first, change these dates, or use a different code.',
  rate_in_use: () => 'This rate is the billing rate of a rent-in, so it cannot be deleted or change kind. Pick another billing rate for the rent-in first.',
  currency_missing: () => "Set the company's currency (CAD or USD) in Settings before adding rates or drafting a ticket.",
  currency_not_settable: () => "A rate is in the company's currency; it is not set per rate.",
  third_party_rate: () => 'A third-party rate carries no rate: the vendor\'s cost is entered on each ticket line. Pick at cost or a markup % instead.',
  markup_not_markup: () => 'A markup % applies only when the pricing is cost plus a markup.',
  pricing_not_third_party: () => 'At cost / markup pricing applies to a third-party rate only.',
  crew_position_not_people: () => 'A crew position applies to an additional-crew or subsistence rate only.',
  equipment_not_equipment_kind: () => 'A unit or equipment type applies to an equipment rate only.',
  equipment_unit_or_type: () => 'An equipment rate prices one unit or one equipment type. Pick exactly one.',
  fuel_unit: () => 'A fuel rate is per volume, in litres or gallons — the units a tour sheet records fuel in.',
  volume_not_per_volume: () => 'A volume unit applies to a per-volume rate only.',
  rate_not_rent_in: () => 'A rent-in bills at a rented-in equipment or other rate. Pick a rate of that kind.',
  rate_other_customer: () => "That rate belongs to a different customer than this job's.",
  rate_other_currency: () => "That rate is in a different currency from this ticket's.",
  rate_unit_mismatch: () => "That rate is counted in a different unit from this line. Pick a rate with the line's unit.",
  rate_kind_mismatch: () => "That rate prices a different kind of work from this line. Pick a rate of the line's kind.",
  unit_fixed: () => 'A line drafted from the tour sheets keeps the unit its hours, days or sheets were counted in.',
  not_third_party: () => 'A vendor cost and markup apply to a third-party line only.',
  // drafting
  work_date_required: () => 'This job is ticketed per day. Pick the day to ticket.',
  work_date_not_per_day: () => 'This job is ticketed per job: one ticket covers every day, so no day is picked.',
  no_tour: (x) => str(x.error) ?? 'There is no tour sheet for that day. A ticket is drafted from the day\'s approved tour sheet.',
  tour_not_approved: (x) => str(x.error) ?? 'That day\'s tour sheet is not approved yet. The office approves the sheet before it is ticketed.',
  tour_ticketed: (x) => str(x.error) ?? 'That tour sheet is already on a ticket.',
  no_approved_tours: () => 'This job has no office-approved tour sheet that is not already on a ticket.',
  rig_missing: () => 'This job has no rig. Name the rig on the job before drafting a ticket.',
  ticket_exists: () => 'This job already has a ticket for that. Open it from the list below.',
  ticket_scope_conflict: (x) =>
    str(x.error) ?? 'This job already has a ticket drafted the other way (per job or per day). A job is ticketed one way, so the work is not billed twice.',
  // editing and approving
  ticket_customer_approved: () => "The customer has approved this ticket, so what it bills can't change.",
  ticket_void: () => 'This ticket is void, so it can no longer be changed.',
  ticket_changed: () => 'This ticket changed while you were editing it. Reload it and try again.',
  ticket_transition_not_allowed: () => 'This ticket has already moved on. Reload it to see where it is.',
  ticket_unpriced: (x) => {
    const n = Array.isArray(x.lines) ? x.lines.length : 0;
    return n === 1
      ? 'One line has no rate. Price it or remove it before the ticket can be approved.'
      : `${n || 'Some'} lines have no rate. Price them or remove them before the ticket can be approved.`;
  },
  ticket_empty: () => 'This ticket has no lines. Refresh it from the tour sheets or add a line before approving it.',
  tax_unconfigured: (x) => str(x.error) ?? 'Tax is not configured for where this well is. Set it in Settings → Regional & Tax.',
  reason_required: () => 'Say why the ticket is being voided.',
  // WS-5: a ticket on a live invoice cannot be voided; the invoice is voided first.
  ticket_invoiced: (x) =>
    `This ticket is on invoice ${str(x.invoice_number) ?? '—'}, so it cannot be voided or changed. Void the invoice first; that releases the ticket.`,
  // sending
  ticket_not_office_approved: () => 'Approve the ticket as the office first.',
  no_customer_email: () => "This customer has no email address on file. Enter the company man's email to send the ticket.",
  email_invalid: () => 'Enter a valid email address.',
  cc_invalid: (x) => str(x.error) ?? 'One of the copy addresses is not a valid email.',
  email_send_failed: () => 'The email could not be sent. The review link below still works; copy it to the company man.',
  // the photo of the signed paper ticket
  approval_already_recorded: () => 'This ticket already carries a recorded customer approval. It cannot be replaced.',
  date_in_future: () => "The day it was signed can't be in the future.",
  date_before_office_approval: () => "The day it was signed can't be before the office approved the ticket.",
  invalid_upload: () => 'That upload is not a file filed against this ticket. Attach the file again.',
  file_type_invalid: () => 'Attach a PDF, PNG or JPEG file.',
  file_required: () => 'Attach the photo of the signed ticket.',
  file_too_large: () => 'The file is larger than 20 MB.',
  approved_by_name_required: () => 'Enter who signed the ticket.',
  approved_on_required: () => 'Enter the day the ticket was signed.',
  text_too_long: () => 'That text is too long.',
};

export const WS_TICKET_REFUSAL_CODES = Object.keys(WS_TICKET_REFUSALS);

/**
 * A WS-4 write's error as one plain sentence: a WS-4 code from the map, else
 * the WS-2 map and its 403 / 404 sentences (wsJobRefusal), else the API's own
 * `error`, else `fallback`.
 */
export function wsTicketRefusal(err: unknown, fallback: string): string {
  const d = (err as { response?: { data?: unknown } })?.response?.data;
  const x = d && typeof d === 'object' ? (d as Record<string, unknown>) : {};
  const code = typeof x.code === 'string' ? x.code : null;
  if (code && WS_TICKET_REFUSALS[code]) return WS_TICKET_REFUSALS[code](x);
  return wsJobRefusal(err, fallback);
}

/** The invoice a `ticket_invoiced` refusal names (WS-5), so the page can link to it; null for any other refusal. */
export function wsTicketInvoicedFrom(err: unknown): { id: string; number: string } | null {
  const d = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
  if (!d || d.code !== 'ticket_invoiced') return null;
  const id = str(d.invoice_id);
  return id ? { id, number: str(d.invoice_number) ?? 'the invoice' } : null;
}

const body = (err: unknown): Record<string, unknown> => {
  const d = (err as { response?: { data?: unknown } })?.response?.data;
  return d && typeof d === 'object' ? (d as Record<string, unknown>) : {};
};

/** The lines a `ticket_unpriced` refusal names (id + description). Empty for any other error. */
export function unpricedLinesFrom(err: unknown): { id: string; description: string }[] {
  const d = body(err);
  if (d.code !== 'ticket_unpriced' || !Array.isArray(d.lines)) return [];
  return (d.lines as unknown[])
    .filter((l): l is { id: string; description?: unknown } => !!l && typeof (l as { id?: unknown }).id === 'string')
    .map((l) => ({ id: l.id, description: typeof l.description === 'string' && l.description ? l.description : '—' }));
}

/** The review link a 502 `email_send_failed` still carries (data.reviewUrl). */
export function reviewUrlFrom(err: unknown): string | null {
  const d = body(err).data as { reviewUrl?: unknown } | undefined;
  return d && typeof d.reviewUrl === 'string' && d.reviewUrl ? d.reviewUrl : null;
}

/** `$450.00/day` style suffix for a rate's unit; distance names km or mi, volume its unit, when known. */
export function wsRateUnitSuffix(unit: string | null | undefined, distanceUnit?: string | null, volumeUnit?: string | null): string {
  switch (unit) {
    case 'per_hour':
      return '/hr';
    case 'per_day':
      return '/day';
    case 'per_tour':
      return '/tour sheet';
    case 'each':
      return ' each';
    case 'per_distance':
      return distanceUnit ? `/${distanceUnit}` : '/distance';
    case 'per_volume':
      return volumeUnit ? `/${volumeUnit}` : '/volume';
    case 'flat':
      return ' flat';
    default:
      return '';
  }
}
